import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { opportunityService } from '../services/api';

const CLOUD_SERVICES = [
  'AWS Migration', 'Azure Migration', 'Google Cloud Migration',
  'Multi-Cloud Strategy', 'Cloud Security', 'DevOps & CI/CD',
  'Kubernetes & Containers', 'Data Analytics', 'Machine Learning/AI', 'Disaster Recovery'
];

const CURRENT_ENVIRONMENTS = [
  'On-Premises', 'AWS', 'Azure', 'Google Cloud', 'Other Cloud Provider', 'Hybrid', 'None'
];

const PARTNER_COUNTRIES = [
  'Australia', 'Bangladesh', 'Cambodia', 'China', 'Hong Kong', 'India',
  'Indonesia', 'Japan', 'Malaysia', 'Myanmar', 'Nepal', 'New Zealand',
  'Pakistan', 'Philippines', 'Singapore', 'South Korea', 'Sri Lanka',
  'Taiwan', 'Thailand', 'UAE', 'Vietnam', 'Other'
];

const DEAL_TYPES = {
  'Professional Services': 'PS',
  'Managed Services': 'MS',
  'Resell': 'Resell',
  'PSO - Resource Augmentation': 'PSO'
};
const PS_SUB_CATEGORIES = ['Infrastructure Services', 'Data & GenAI'];
const RESELL_SUB_CATEGORIES = ['CDN', 'License Resell', 'AWS Resell'];
const DEAL_VALUE_TYPES = ['Customer', 'OEM'];
const REVENUE_SHARE_PRESETS = [5, 10, 15];

const EMPTY_FORM = {
  partner_company: '',
  partner_country: '',
  company_name: '',
  contact_first_name: '', contact_last_name: '',
  contact_email: '', contact_phone: '', contact_mobile: '',
  opportunity_name: '',
  cloud_services: [],
  arr_usd: '',
  estimated_close_date: '',
  current_environment: '',
  business_challenge: '', technical_requirements: '',
  deal_type: '',
  deal_value_type: '',
  resell_sub_category: '',
  ps_sub_category: ''
};

export default function OpportunityForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [customService, setCustomService] = useState('');
  const [addingService, setAddingService] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAuthSession().then(session => {
      const payload = session.tokens?.idToken?.payload;
      const company = payload?.['custom:company'] || '';
      const country = payload?.['custom:country'] || '';
      const email = payload?.email || '';
      const firstName = payload?.given_name || '';
      const lastName = payload?.family_name || '';

      setUserInfo({
        company,
        country,
        email,
        name: [firstName, lastName].filter(Boolean).join(' ') || email
      });

      // Auto-populate partner info in form
      setForm(prev => ({
        ...prev,
        partner_company: company,
        partner_country: country
      }));
    });
  }, []);

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'cloud_services') {
      setField('cloud_services', checked
        ? [...form.cloud_services, value]
        : form.cloud_services.filter(s => s !== value));
    } else {
      setField(name, value);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const result = await opportunityService.create(form);
      setMessage({ type: 'success', text: 'Opportunity submitted successfully!' });
      setTimeout(() => navigate(`/opportunities/${result.opportunityId}`), 1200);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to submit' });
    } finally {
      setLoading(false);
    }
  };

  const showResellSub = form.deal_type === 'Resell';
  const showPSSub = form.deal_type === 'PS';

  return (
    <div className="form-container">
      <h2>Submit Opportunity</h2>

      {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

      <form onSubmit={handleSubmit}>
        {/* Partner Details */}
        <section className="form-section">
          <h3>Deal Owner</h3>
          {userInfo ? (
            <div className="user-info-display">
              <div className="user-info-item">
                <span className="label">Salesperson:</span>
                <span className="value">{userInfo.name}</span>
              </div>
              <div className="user-info-item">
                <span className="label">Email:</span>
                <span className="value">{userInfo.email}</span>
              </div>
              <div className="user-info-item">
                <span className="label">Partner:</span>
                <span className="value">{userInfo.company}</span>
              </div>
              <div className="user-info-item">
                <span className="label">Location:</span>
                <span className="value">{userInfo.country}</span>
              </div>
            </div>
          ) : (
            <div className="loading">Loading user info...</div>
          )}
        </section>

        {/* Customer Info */}
        <section className="form-section">
          <h3>Customer / Prospect</h3>
          <div className="form-group">
            <label>Customer Company Name *</label>
            <input name="company_name" value={form.company_name} onChange={handleChange} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input name="contact_first_name" value={form.contact_first_name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input name="contact_last_name" value={form.contact_last_name} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input type="email" name="contact_email" value={form.contact_email} onChange={handleChange} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input type="tel" name="contact_phone" value={form.contact_phone} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Mobile</label>
              <input type="tel" name="contact_mobile" value={form.contact_mobile} onChange={handleChange} />
            </div>
          </div>
        </section>

        {/* Opportunity Details */}
        <section className="form-section">
          <h3>Opportunity Details</h3>
          <div className="form-group">
            <label>Opportunity Name *</label>
            <input name="opportunity_name" value={form.opportunity_name} onChange={handleChange} required placeholder="e.g. Acme Corp – AWS Migration" />
          </div>

          <div className="form-group">
            <label>Cloud Services of Interest *</label>
            <div className="service-picker">
              {[...CLOUD_SERVICES, ...form.cloud_services.filter(s => !CLOUD_SERVICES.includes(s))].map(s => {
                const selected = form.cloud_services.includes(s);
                const isCustom = !CLOUD_SERVICES.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    className={`service-option${selected ? ' service-selected' : ''}${isCustom ? ' service-custom' : ''}`}
                    onClick={() => setField('cloud_services', selected
                      ? form.cloud_services.filter(x => x !== s)
                      : [...form.cloud_services, s]
                    )}
                  >
                    {selected && <span className="service-check">✓</span>}
                    {s}
                    {isCustom && (
                      <span className="service-remove" onClick={e => {
                        e.stopPropagation();
                        setField('cloud_services', form.cloud_services.filter(x => x !== s));
                      }}>×</span>
                    )}
                  </button>
                );
              })}
              {addingService && (
                <div className="add-service-inline">
                  <input
                    type="text"
                    className="add-service-input"
                    placeholder="Service name…"
                    value={customService}
                    autoFocus
                    onChange={e => setCustomService(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = customService.trim();
                        if (val && !form.cloud_services.includes(val)) setField('cloud_services', [...form.cloud_services, val]);
                        setCustomService('');
                        setAddingService(false);
                      }
                      if (e.key === 'Escape') { setCustomService(''); setAddingService(false); }
                    }}
                    onBlur={() => {
                      const val = customService.trim();
                      if (val && !form.cloud_services.includes(val)) setField('cloud_services', [...form.cloud_services, val]);
                      setCustomService('');
                      setAddingService(false);
                    }}
                  />
                </div>
              )}
              {!addingService && (
                <button
                  type="button"
                  className="service-option service-add-new"
                  onClick={() => setAddingService(true)}
                >
                  + Add new service
                </button>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>ARR (USD) *</label>
              <input type="number" name="arr_usd" value={form.arr_usd} onChange={handleChange} min="0" step="1000" required placeholder="Annual Recurring Revenue in USD" />
            </div>
            <div className="form-group">
              <label>Estimated Close Date *</label>
              <input type="date" name="estimated_close_date" value={form.estimated_close_date} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-group">
            <label>Current Environment *</label>
            <select name="current_environment" value={form.current_environment} onChange={handleChange} required>
              <option value="">Select…</option>
              {CURRENT_ENVIRONMENTS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Primary Business Challenge *</label>
            <textarea name="business_challenge" value={form.business_challenge} onChange={handleChange} rows="4" required />
          </div>

          <div className="form-group">
            <label>Technical Requirements / Scope</label>
            <textarea name="technical_requirements" value={form.technical_requirements} onChange={handleChange} rows="4" />
          </div>
        </section>

        {/* Deal Classification */}
        <section className="form-section">
          <h3>Deal Classification</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Deal Type</label>
              <select name="deal_type" value={form.deal_type} onChange={handleChange}>
                <option value="">Select…</option>
                {Object.entries(DEAL_TYPES).map(([label, value]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Deal Source</label>
              <select name="deal_value_type" value={form.deal_value_type} onChange={handleChange}>
                <option value="">Select…</option>
                {DEAL_VALUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {showPSSub && (
            <div className="form-group">
              <label>PS Sub-Category</label>
              <select name="ps_sub_category" value={form.ps_sub_category} onChange={handleChange}>
                <option value="">Select…</option>
                {PS_SUB_CATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}

          {showResellSub && (
            <div className="form-group">
              <label>Resell Sub-Category</label>
              <select name="resell_sub_category" value={form.resell_sub_category} onChange={handleChange}>
                <option value="">Select…</option>
                {RESELL_SUB_CATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
        </section>

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Submitting…' : 'Submit Opportunity'}
          </button>
        </div>
      </form>
    </div>
  );
}
